﻿import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { CreateEventRequest, Event, UpdateEventRequest } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useCreateEventMutation } from "@/features/events/api";
import {
  EventForm,
  type CreateEventFormValues,
} from "@/features/events/event-form";
import { localInputToIso } from "@/features/events/date-helpers";
import { ApiError, apiClient, uploadImage } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

export function NewEventPage() {
  const t = useT("newEvent");
  const tDetail = useT("eventDetail");
  const navigate = useNavigate();
  const mutation = useCreateEventMutation();
  // mutation.isPending sadece create adımını kapsıyor; cover upload + patch sırasında
  // butonun "Kaydediliyor..." kalması için ek flag tutuyoruz.
  const [isPostProcessing, setIsPostProcessing] = useState(false);

  const onSubmit = async (
    values: CreateEventFormValues,
    coverFile: File | null,
  ) => {
    const payload: CreateEventRequest = {
      title: values.title,
      description: values.description || null,
      coverImageUrl: null,
      slug: values.slug || null,
      venueName: values.venueName || null,
      venueAddress: values.venueAddress || null,
      venueCity: values.venueCity || null,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      startsAt: localInputToIso(values.startsAt)!,
      endsAt: localInputToIso(values.endsAt)!,
      defaultTaxRate: values.defaultTaxRate,
    };

    let evt: Event;
    try {
      evt = await mutation.mutateAsync(payload);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("createFailed"));
      return;
    }

    if (!coverFile) {
      toast.success(t("created"));
      navigate(`/events/${evt.slug}`);
      return;
    }

    // Event oluştu, kapak dosyasını upload edip URL'i PATCH ile yazıyoruz. Bu adımlar
    // başarısız olsa bile event mevcut → kullanıcıyı detay sayfasına yönlendirip orada
    // tekrar denemesini bekliyoruz, manuel temizlik gerekmiyor.
    setIsPostProcessing(true);
    try {
      const uploaded = await uploadImage({
        file: coverFile,
        kind: "EventCover",
        eventId: evt.id,
      });
      const updateBody: UpdateEventRequest = {
        title: evt.title,
        description: evt.description ?? null,
        coverImageUrl: uploaded.url,
        venueName: evt.venueName ?? null,
        venueAddress: evt.venueAddress ?? null,
        venueCity: evt.venueCity ?? null,
        latitude: evt.latitude ?? null,
        longitude: evt.longitude ?? null,
        startsAt: evt.startsAt,
        endsAt: evt.endsAt,
        defaultTaxRate: evt.defaultTaxRate,
      };
      await apiClient<Event>(`/events/${evt.slug}`, {
        method: "PUT",
        body: updateBody,
      });
      toast.success(t("created"));
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : t("coverFailed");
      toast.warning(t("coverWarning", { detail }));
    } finally {
      setIsPostProcessing(false);
      navigate(`/events/${evt.slug}`);
    }
  };

  return (
    <DashboardPage>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/events">
            <ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" /> {tDetail("back")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("title")}</CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <EventForm
            mode="create"
            onSubmit={onSubmit}
            isSubmitting={mutation.isPending || isPostProcessing}
          />
        </CardContent>
      </Card>
    </DashboardPage>
  );
}
